import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../store/hooks/reduxHooks';
import { handleLogout } from '../utils/sessionManager';

interface SessionTimeoutModalProps {
  warningTimeMS: number;  // Time in ms before session expiry to show warning
  logoutTimeMS: number;   // Total time in ms for session timeout
  onContinue: () => void; // Callback to reset the timer
  isVisible: boolean;     // Whether the modal is visible
}

const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  warningTimeMS,
  logoutTimeMS,
  onContinue,
  isVisible
}) => {
  const dispatch = useAppDispatch();
  const [countdown, setCountdown] = useState(Math.floor((logoutTimeMS - warningTimeMS) / 1000));
  
  // Format the countdown time as mm:ss
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  // Handle countdown
  useEffect(() => {
    if (!isVisible) {
      return;
    }
    
    let intervalId: NodeJS.Timeout;
    
    if (countdown > 0) {
      intervalId = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else {
      // Logout when countdown reaches zero
      handleLogout(dispatch);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [countdown, dispatch, isVisible]);
  
  // Reset countdown when visibility changes
  useEffect(() => {
    if (isVisible) {
      setCountdown(Math.floor((logoutTimeMS - warningTimeMS) / 1000));
    }
  }, [isVisible, logoutTimeMS, warningTimeMS]);
  
  // Handle continue session
  const handleContinue = useCallback(() => {
    onContinue();
  }, [onContinue]);
  
  // Handle manual logout
  const handleLogoutClick = useCallback(() => {
    handleLogout(dispatch);
  }, [dispatch]);
  
  if (!isVisible) {
    return null;
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-gradient-blue-1 to-gradient-blue-2 p-4">
          <h2 className="text-xl font-gilroy-bold text-white">Session Timeout Warning</h2>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-brand-light flex items-center justify-center">
              <span className="text-2xl font-gilroy-bold text-brand-blue">{formatTime(countdown)}</span>
            </div>
          </div>
          
          <p className="text-center mb-6 text-brand-dark font-gilroy-medium">
            Your session is about to expire due to inactivity. 
            You will be automatically logged out in <span className="font-gilroy-bold text-brand-blue">{formatTime(countdown)}</span>.
          </p>
          
          <div className="flex gap-4">
            <button
              onClick={handleLogoutClick}
              className="flex-1 py-3 px-4 rounded-lg border border-gray-300 text-brand-gray font-gilroy-medium hover:bg-gray-50 transition"
            >
              Logout
            </button>
            
            <button
              onClick={handleContinue}
              className="flex-1 py-3 px-4 rounded-lg bg-gradient-to-r from-gradient-blue-1 to-gradient-blue-2 text-white font-gilroy-medium hover:shadow-lg transition"
            >
              Continue Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal; 